/**
 * Admin shell constants: rail geometry, chrome copy and outbound links.
 *
 * Every measurement here is the customer sidebar's existing value, not a new scale — the
 * admin rail is meant to read as the same chrome one level up (R9).
 */

/** Collapsed rail: icon-only, matching the customer sidebar's 72px. */
export const RAIL_COLLAPSED_CLASSES = "w-[72px] px-4";

/** Expanded rail: labels visible, matching the customer sidebar's 260px. */
export const RAIL_EXPANDED_CLASSES = "w-[260px] px-2.5";

/** Pixel size of the mascot mark in the rail head — same collapsed or expanded (R9). */
export const RAIL_MARK_SIZE = 30;

/**
 * Rail-head mascots, one per tool.
 *
 * Not the plain brand mark: each rail names its own tool rather than the product, so it shows the
 * mascot that matches — the one at a laptop for the admin dashboard, the one holding a bone for
 * Fetch, which is the sourcing tool's own long-standing motif.
 *
 *
 * The `-v2` on the Metrics file is a cache-bust, not a variant. Metrics used to wear the mascot
 * Finances wears now, and files under `public/` are served at a stable URL with no fingerprint,
 * so replacing the bytes in place left every browser that had already loaded the page showing the
 * old dog. Changing the path is the only thing that reliably moves them. If a mascot is swapped
 * again, give it a new filename rather than overwriting one.
 */
export const RAIL_MARK_SRC_ADMIN = "/nowadays-admin-mark.png";
export const RAIL_MARK_SRC_FETCH = "/nowadays-fetch-mark.png";
export const RAIL_MARK_SRC_METRICS = "/nowadays-metrics-mark-v2.png";
export const RAIL_MARK_SRC_FINANCES = "/nowadays-finances-mark.png";
export const RAIL_MARK_SRC_PLANNING = "/nowadays-planning-mark.png";
/** The Venues mascot: the heart-glasses dog from the `:nowadays-heartglasses:` emoji. */
export const RAIL_MARK_SRC_VENUES = "/nowadays-venues-mark.png";

/**
 * Per-tool favicons, cut from the same mascots.
 *
 * Admin tabs are indistinguishable from customer tabs otherwise, and ops keeps both open at
 * once. The root layout sets `icons` explicitly, so these are applied as metadata rather than
 * through Next's `icon.png` file convention — an explicit parent value wins over a child file.
 */
export const FAVICON_ADMIN = "/favicon-admin.png";
export const FAVICON_FETCH = "/favicon-fetch.png";
export const FAVICON_METRICS = "/favicon-metrics-v2.png";
export const FAVICON_PLANNING = "/favicon-planning.png";
/** The Venues tab icon, cut from the same mascot. */
export const FAVICON_VENUES = "/favicon-venues.png";

/** Where the user menu sends an operator who wants the client-facing app. */
export const CUSTOMER_DASHBOARD_PATH = "/dashboard";

/** Sign-out route. */
export const LOGOUT_PATH = "/logout";

/** Shown on the rail's inert Search row, so its disabled state explains itself. */
export const SEARCH_DISABLED_TOOLTIP = "Search is coming soon";

/** Tooltip on the live Search row, for the viewers who have the admin palette. */
export const SEARCH_ENABLED_TOOLTIP = "Search admin tools and pages";

/** The key shown after the platform modifier in the Search row's shortcut hint (Cmd-K / Ctrl-K). */
export const SEARCH_HOTKEY_LABEL = "K";

/**
 * Search field inside a rail popover, stripped of every focus decoration.
 *
 * These fields autofocus the moment their popover opens, so `FetchInput`'s indigo ring and glow
 * fired on every open — a "you are here" signal on the one element that is always there. Marked
 * important so no ordering or specificity surprise in the shared input can bring the ring back.
 */
export const RAIL_POPOVER_SEARCH_INPUT_CLASS =
  "rounded-none border-0 bg-transparent text-sm shadow-none " +
  "focus:!border-transparent focus:!shadow-none focus:!ring-0 focus:!outline-none";

/** How long an ID chip shows its "copied" check before going back to the copy icon. */
export const ADMIN_ID_COPIED_RESET_MS = 1500;

/** The "Show IDs" / "Hide IDs" row's label while IDs are hidden (account menu and Cmd-K). */
export const SHOW_IDS_MENU_LABEL = "Show IDs";

/** The "Show IDs" / "Hide IDs" row's label while IDs show (account menu and Cmd-K). */
export const HIDE_IDS_MENU_LABEL = "Hide IDs";

/** The account menu row that copies the current event page's client dashboard URL. */
export const COPY_CLIENT_LINK_MENU_LABEL = "Copy client link";

/** The toast once the account menu's client link is on the clipboard. */
export const CLIENT_LINK_COPIED_MESSAGE = "Client link copied";

/** The toast when the clipboard refused the account menu's client link. */
export const CLIENT_LINK_COPY_FAILED_MESSAGE = "Couldn't copy to the clipboard";
